import React, { useState, useEffect } from "react";
import { Upload, FileText, Trash2, AlertCircle, CheckCircle, Loader, X, Globe, Database } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { apiClient } from "../services/api";
import webAuthService from "../services/webAuthService";

function KnowledgeBase() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [urlInput, setUrlInput] = useState("");
  const [scrapingUrl, setScrapingUrl] = useState(false);

  // External Qdrant collections state
  const [extCollections, setExtCollections] = useState([]);
  const [loadingExtCollections, setLoadingExtCollections] = useState(false);
  const [addingCollection, setAddingCollection] = useState(false);
  const [extForm, setExtForm] = useState({
    display_name: "",
    qdrant_url: "",
    qdrant_port: "6333",
    collection_name: "",
    api_key: "",
    description: "",
    embedder_model: "text-embedding-3-large",
    embedder_dimensions: "1024",
    embedder_provider: "azure",
  });
  
  // Extract user details from MSAL
  const [userDetails, setUserDetails] = useState({
    userId: "unknown",
    userName: "User",
    departmentId: "General",
    departmentName: "General"
  });

  useEffect(() => {
    const loadUserDetails = async () => {
      try {
        await webAuthService.initialize();
        const account = webAuthService.msalInstance?.getActiveAccount();

        let newUserDetails;
        if (account) {
          // Extract department from idTokenClaims
          const claims = account.idTokenClaims || {};
          const departmentId = claims.extension_Department || claims.department || claims.jobTitle || "General";
          const departmentName = departmentId;
          newUserDetails = {
            userId: account.localAccountId || account.homeAccountId || "unknown",
            userName: account.name || account.username || "User",
            departmentId,
            departmentName,
          };
        } else {
          // Fall back to SSO profile
          const ssoRaw = localStorage.getItem("userProfileData");
          if (ssoRaw) {
            const ssoProfile = JSON.parse(ssoRaw);
            if (ssoProfile?.source === "sso" && ssoProfile?.id) {
              newUserDetails = {
                userId: ssoProfile.id,
                userName: ssoProfile.name || "User",
                departmentId: "General",
                departmentName: "General",
              };
            }
          }
        }

        if (newUserDetails) {
          setUserDetails(newUserDetails);
          fetchFilesForUser(newUserDetails.userId);
          fetchExtCollections(newUserDetails.userId);
        }
      } catch (err) {
        console.error("Error loading user details:", err);
      }
    };
    
    loadUserDetails();
  }, []);

  const fetchFilesForUser = async (userId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/api/knowledge-base/user/${userId}`);
      setFiles(response.data.files || []);
    } catch (err) {
      console.error("Error fetching files:", err);
      setError("Failed to load knowledge base files");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchFiles = () => {
    fetchFilesForUser(userDetails.userId);
  };

  // External collections CRUD
  const fetchExtCollections = async (userId) => {
    try {
      setLoadingExtCollections(true);
      const response = await apiClient.get(`/api/knowledge-base/external-collections/${userId}`);
      setExtCollections(response.data.collections || []);
    } catch (err) {
      console.error("Error fetching external collections:", err);
    } finally {
      setLoadingExtCollections(false);
    }
  };

  const handleAddExtCollection = async () => {
    if (!extForm.qdrant_url || !extForm.collection_name) {
      setError("Qdrant URL and Collection Name are required");
      return;
    }
    try {
      setAddingCollection(true);
      setError(null);
      const response = await apiClient.post("/api/knowledge-base/external-collections", {
        user_id: userDetails.userId,
        display_name: extForm.display_name || extForm.collection_name,
        qdrant_url: extForm.qdrant_url,
        qdrant_port: parseInt(extForm.qdrant_port, 10) || 6333,
        collection_name: extForm.collection_name,
        api_key: extForm.api_key || undefined,
        description: extForm.description || undefined,
        embedder_model: extForm.embedder_model,
        embedder_dimensions: parseInt(extForm.embedder_dimensions, 10) || 1024,
        embedder_provider: extForm.embedder_provider,
      });
      if (response.data.status === "success") {
        setSuccess("External collection added successfully!");
        setExtForm({
          display_name: "", qdrant_url: "", qdrant_port: "6333", collection_name: "",
          api_key: "", description: "", embedder_model: "text-embedding-3-large",
          embedder_dimensions: "1024", embedder_provider: "azure",
        });
        fetchExtCollections(userDetails.userId);
      } else {
        setError(response.data.message || "Failed to add collection");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to add collection");
    } finally {
      setAddingCollection(false);
    }
  };

  const handleDeleteExtCollection = async (collectionId, displayName) => {
    if (!window.confirm(`Remove external collection "${displayName}"?`)) return;
    try {
      await apiClient.delete(`/api/knowledge-base/external-collections/${collectionId}?user_id=${userDetails.userId}`);
      setSuccess(`Collection "${displayName}" removed`);
      fetchExtCollections(userDetails.userId);
    } catch (err) {
      setError("Failed to remove collection");
    }
  };

  // Model preset helper — auto-fill dimensions when model changes
  const EMBEDDER_PRESETS = {
    "text-embedding-3-large": { dimensions: "1024", note: "Up to 3072 dims, 1024 recommended" },
    "text-embedding-3-small": { dimensions: "1536", note: "Up to 1536 dims" },
    "text-embedding-ada-002": { dimensions: "1536", note: "Fixed 1536 dims" },
  };

  const handleModelChange = (model) => {
    const preset = EMBEDDER_PRESETS[model];
    setExtForm((prev) => ({
      ...prev,
      embedder_model: model,
      embedder_dimensions: preset ? preset.dimensions : prev.embedder_dimensions,
    }));
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    
    if (newFiles.length === 0) return;
    
    // Validate file types
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'text/plain',
      'text/html',
      'image/png',
      'image/jpeg',
      'image/tiff'
    ];
    
    const invalidFiles = newFiles.filter(file => !allowedTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setError(`Unsupported file type(s): ${invalidFiles.map(f => f.name).join(', ')}. Please upload PDF, DOCX, PPTX, XLSX, CSV, TXT, HTML, or images.`);
      return;
    }
    
    setSelectedFiles(newFiles);
    setError(null);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError("Please select at least one file to upload");
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);
      setUploadProgress({});

      const formData = new FormData();
      
      // Append multiple files
      selectedFiles.forEach((file) => {
        formData.append("files", file);
      });
      
      // Append user and department info
      formData.append("user_id", userDetails.userId);
      formData.append("user_name", userDetails.userName);
      formData.append("department_id", userDetails.departmentId);
      formData.append("department_name", userDetails.departmentName);

      const response = await apiClient.post("/api/knowledge-base/upload-multiple", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Files are now queued for background processing
      const queuedFiles = response.data?.files || [];
      const taskIds = response.data?.task_ids || [];
      
      setSuccess(`Queued ${queuedFiles.length} file(s) for processing. Files will appear shortly.`);
      setSelectedFiles([]);
      
      // Reset file input
      document.getElementById("file-input").value = "";
      
      // Poll for completion and refresh file list
      if (taskIds.length > 0) {
        setTimeout(() => {
          fetchFiles();
        }, 5000); // Refresh after 5 seconds
      }
    } catch (err) {
      console.error("Error uploading files:", err);
      setError(err.response?.data?.message || "Failed to upload files");
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  const handleDelete = async (fileId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete "${fileName}"? This will remove all associated data from the knowledge base.`)) {
      return;
    }

    try {
      setError(null);
      await apiClient.delete(`/api/knowledge-base/file/${fileId}`);
      setSuccess(`Successfully deleted ${fileName}`);
      await fetchFiles();
    } catch (err) {
      console.error("Error deleting file:", err);
      setError(err.response?.data?.message || "Failed to delete file");
    }
  };

  const handleScrapeUrl = async () => {
    const trimmed = urlInput.trim();
    if (!trimmed) {
      setError("Please enter a URL to scrape");
      return;
    }

    try {
      new URL(trimmed);
    } catch {
      setError("Invalid URL. Please enter a full URL (e.g. https://example.com)");
      return;
    }

    try {
      setScrapingUrl(true);
      setError(null);
      setSuccess(null);

      await apiClient.post("/api/knowledge-base/scrape-url", {
        url: trimmed,
        user_id: userDetails.userId,
        user_name: userDetails.userName,
        department_id: userDetails.departmentId,
        department_name: userDetails.departmentName,
      });

      setSuccess(`URL queued for scraping: ${trimmed}. It will appear in your files shortly.`);
      setUrlInput("");

      // Refresh file list after a delay
      setTimeout(() => {
        fetchFiles();
      }, 5000);
    } catch (err) {
      console.error("Error scraping URL:", err);
      setError(err.response?.data?.message || "Failed to scrape URL");
    } finally {
      setScrapingUrl(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Knowledge Base</h1>
          <p className="text-gray-600">Upload and manage your knowledge base files</p>
        </div>

        {/* Alert Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-300 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-700">
              <X size={20} />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-300 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-green-800 font-medium">Success</p>
              <p className="text-green-700 text-sm mt-1">{success}</p>
            </div>
            <button onClick={() => setSuccess(null)} className="text-green-600 hover:text-green-700">
              <X size={20} />
            </button>
          </div>
        )}

        {/* Upload Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Upload size={24} />
            Upload New File
          </h2>

          <div className="space-y-4">
            {/* File Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Files (Multiple)
              </label>
              <input
                id="file-input"
                type="file"
                multiple
                onChange={handleFileSelect}
                accept=".pdf,.docx,.pptx,.xlsx,.csv,.txt,.html,.png,.jpg,.jpeg,.tiff"
                className="block w-full text-sm text-gray-600
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:text-white
                  file:cursor-pointer cursor-pointer
                  border border-gray-300 rounded-lg bg-white
                  hover:border-gray-400
                  focus:outline-none focus:ring-2 focus:border-transparent"
              />
              <style>{`
                #file-input::file-selector-button {
                  background-color: #e04665;
                }
                #file-input::file-selector-button:hover {
                  background-color: #dc4556;
                }
              `}</style>
              {selectedFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-medium text-gray-700">
                    Selected {selectedFiles.length} file(s):
                  </p>
                  {selectedFiles.map((file, idx) => (
                    <p key={idx} className="text-sm text-gray-600 ml-2">
                      • {file.name} ({formatFileSize(file.size)})
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Upload Button */}
            <button
              onClick={handleUpload}
              disabled={uploading || selectedFiles.length === 0}
              className="w-full disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 shadow-sm"
              style={{ backgroundColor: uploading || selectedFiles.length === 0 ? undefined : '#e04665' }}
              onMouseEnter={(e) => {
                if (!uploading && selectedFiles.length > 0) {
                  e.currentTarget.style.backgroundColor = '#dc4556';
                }
              }}
              onMouseLeave={(e) => {
                if (!uploading && selectedFiles.length > 0) {
                  e.currentTarget.style.backgroundColor = '#e04665';
                }
              }}
            >
              {uploading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  Uploading {selectedFiles.length} file(s)...
                </>
              ) : (
                <>
                  <Upload size={20} />
                  Upload {selectedFiles.length > 0 ? `${selectedFiles.length} file(s)` : ''} to Knowledge Base
                </>
              )}
            </button>
          </div>
        </div>

        {/* URL Scrape Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Globe size={24} />
            Add from URL
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website URL
              </label>
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://example.com/page"
                className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-white px-4 py-2.5 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#e04665] focus:border-transparent"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !scrapingUrl) handleScrapeUrl();
                }}
              />
              <p className="text-xs text-gray-500 mt-1">The page content will be scraped and stored in your knowledge base.</p>
            </div>

            <button
              onClick={handleScrapeUrl}
              disabled={scrapingUrl || !urlInput.trim()}
              className="w-full disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 shadow-sm"
              style={{ backgroundColor: scrapingUrl || !urlInput.trim() ? undefined : '#e04665' }}
              onMouseEnter={(e) => {
                if (!scrapingUrl && urlInput.trim()) {
                  e.currentTarget.style.backgroundColor = '#dc4556';
                }
              }}
              onMouseLeave={(e) => {
                if (!scrapingUrl && urlInput.trim()) {
                  e.currentTarget.style.backgroundColor = '#e04665';
                }
              }}
            >
              {scrapingUrl ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  Scraping URL...
                </>
              ) : (
                <>
                  <Globe size={20} />
                  Scrape &amp; Add to Knowledge Base
                </>
              )}
            </button>
          </div>
        </div>

        {/* External Qdrant Collections Section */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Database size={24} />
            External Qdrant Collections
          </h2>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                <input
                  type="text" value={extForm.display_name}
                  onChange={(e) => setExtForm({ ...extForm, display_name: e.target.value })}
                  placeholder="My Product Knowledge" className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Collection Name *</label>
                <input
                  type="text" value={extForm.collection_name}
                  onChange={(e) => setExtForm({ ...extForm, collection_name: e.target.value })}
                  placeholder="product_docs" className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Qdrant URL *</label>
                <input
                  type="url" value={extForm.qdrant_url}
                  onChange={(e) => setExtForm({ ...extForm, qdrant_url: e.target.value })}
                  placeholder="http://my-qdrant-server.com" className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
                <input
                  type="number" value={extForm.qdrant_port}
                  onChange={(e) => setExtForm({ ...extForm, qdrant_port: e.target.value })}
                  placeholder="6333" className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">API Key (optional)</label>
              <input
                type="password" value={extForm.api_key}
                onChange={(e) => setExtForm({ ...extForm, api_key: e.target.value })}
                placeholder="qdrant-api-key-if-required" className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Embedder Configuration */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Embedding Model (must match how this collection was built)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Provider</label>
                  <select
                    value={extForm.embedder_provider}
                    onChange={(e) => setExtForm({ ...extForm, embedder_provider: e.target.value })}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="azure">Azure OpenAI</option>
                    <option value="openai">OpenAI</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Model</label>
                  <select
                    value={extForm.embedder_model}
                    onChange={(e) => handleModelChange(e.target.value)}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="text-embedding-3-large">text-embedding-3-large</option>
                    <option value="text-embedding-3-small">text-embedding-3-small</option>
                    <option value="text-embedding-ada-002">text-embedding-ada-002</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Dimensions</label>
                  <input
                    type="number" value={extForm.embedder_dimensions}
                    onChange={(e) => setExtForm({ ...extForm, embedder_dimensions: e.target.value })}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  {EMBEDDER_PRESETS[extForm.embedder_model] && (
                    <p className="text-[10px] text-gray-500 mt-1">{EMBEDDER_PRESETS[extForm.embedder_model].note}</p>
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={handleAddExtCollection}
              disabled={addingCollection || !extForm.qdrant_url.trim() || !extForm.collection_name.trim()}
              className="w-full disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 shadow-sm bg-indigo-600 hover:bg-indigo-700"
              style={addingCollection || !extForm.qdrant_url.trim() || !extForm.collection_name.trim() ? {} : {}}
            >
              {addingCollection ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  Connecting & Adding...
                </>
              ) : (
                <>
                  <Database size={20} />
                  Connect &amp; Add Collection
                </>
              )}
            </button>
          </div>

          {/* Saved External Collections List */}
          {extCollections.length > 0 && (
            <div className="mt-6 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">Saved Collections ({extCollections.length})</h3>
              {extCollections.map((col) => (
                <div
                  key={col.id}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:bg-gray-100 transition-all"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-50">
                      <Database size={20} className="text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{col.display_name}</h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-600 flex-wrap">
                        <span>{col.collection_name}</span>
                        <span>•</span>
                        <span className="truncate max-w-xs">{col.qdrant_url}:{col.qdrant_port}</span>
                        <span>•</span>
                        <span className="text-indigo-600 text-xs font-medium">
                          {col.embedder_model || "text-embedding-3-large"} ({col.embedder_dimensions || 1024}d)
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteExtCollection(col.id, col.display_name)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-all"
                    title="Remove collection"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Files List */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText size={24} />
            Your Files ({files.length})
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="animate-spin" style={{ color: '#e04665' }} size={32} />
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No files uploaded yet</p>
              <p className="text-gray-500 text-sm mt-2">Upload your first file to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {files.map((file) => (
                <div
                  key={file.id}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:bg-gray-100 hover:border-gray-300 transition-all"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#e0466520' }}>
                      {file.source_type === "url" ? (
                        <Globe size={20} style={{ color: '#e04665' }} />
                      ) : (
                        <FileText size={20} style={{ color: '#e04665' }} />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{file.file_name}</h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 flex-wrap">
                        {file.source_type === "url" && file.source_url && (
                          <>
                            <span className="text-blue-600 truncate max-w-xs" title={file.source_url}>URL</span>
                            <span>•</span>
                          </>
                        )}
                        <span>{file.department_name}</span>
                        <span>•</span>
                        <span>{formatDate(file.uploaded_at)}</span>
                        <span>•</span>
                        <span>{file.chunks_count || 0} chunks</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(file.id, file.file_name)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-all"
                    title="Delete file"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default KnowledgeBase;
